import { ButtonProps, Text } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import SharedSelect from "../../../shared/components/Select";
import useApi from "../../../shared/hooks/useApi";
import { assertNever } from "../../../utils";
import { entityProfileModalColors } from "../entityProfileModal.theme";

export type EntityProfileLookupEntity =
  | "agency-members"
  | "caregivers"
  | "offices"
  | "patients"
  | "referral-sources"
  | "social-workers";

export type EntityProfileLookupOption = {
  certifications?: string[];
  description?: string | null;
  displayId?: number | string | null;
  id: number;
  displayName: string;
  email?: string | null;
  isActive?: boolean;
  jobTitle?: string | null;
  phoneNumber?: string | null;
  photoUrl?: string | null;
  status?: string | null;
  type?: string | null;
};

type EntityProfileLookupResponse = {
  items: EntityProfileLookupOption[];
};

type EntityProfileLookupQuery = {
  certifications?: string;
  officeId?: number | string;
  roles?: string;
  status?: string;
  type?: string;
};

function formatLookupLabel(option: EntityProfileLookupOption) {
  if (option.displayId === null || option.displayId === undefined || option.displayId === "") {
    return option.displayName;
  }

  return `${option.displayName} (${option.displayId})`;
}

function mergeSelectedOption(
  options: EntityProfileLookupOption[],
  selectedOption: EntityProfileLookupOption | null | undefined
) {
  if (selectedOption === null || selectedOption === undefined) {
    return options;
  }

  if (options.some((option) => option.id === selectedOption.id)) {
    return options;
  }

  return [selectedOption, ...options];
}

function getLookupDescription(option: EntityProfileLookupOption) {
  return (
    option.description ??
    option.jobTitle ??
    option.email ??
    option.phoneNumber ??
    option.type ??
    option.status ??
    undefined
  );
}

function formatQueryNumber(value: number | string | undefined) {
  return value === undefined ? undefined : String(value);
}

export default function EntityProfileLookupSelect(props: {
  allowUnselect?: boolean;
  buttonProps?: ButtonProps;
  entity: EntityProfileLookupEntity;
  isDisabled?: boolean;
  label: string;
  query?: EntityProfileLookupQuery;
  selectedOption?: EntityProfileLookupOption | null;
  value: number | null;
  width?: string;
  onChange: (option: EntityProfileLookupOption | null) => void;
}) {
  const { api } = useApi();
  const searchTermState = React.useState("");
  const [searchTerm] = searchTermState;
  const lookupQuery = useQuery({
    queryKey: ["entityProfileModal", "lookup", props.entity, searchTerm, props.query],
    queryFn: async (): Promise<EntityProfileLookupResponse> => {
      const q = searchTerm.trim() === "" ? undefined : searchTerm.trim();

      switch (props.entity) {
        case "agency-members":
          return api.get("./search/agency-members", {
            query: {
              limit: 20,
              officeId: formatQueryNumber(props.query?.officeId),
              q,
              roles: props.query?.roles,
              status: props.query?.status,
            },
          });
        case "caregivers":
          return api.get("./search/caregivers", {
            query: {
              certifications: props.query?.certifications,
              limit: 20,
              officeId: formatQueryNumber(props.query?.officeId),
              q,
              status: props.query?.status,
            },
          });
        case "offices":
          return api.get("./search/offices", {
            query: {
              limit: 20,
              q,
              status: props.query?.status,
            },
          });
        case "patients":
          return api.get("./search/patients", {
            query: {
              limit: 20,
              officeId: formatQueryNumber(props.query?.officeId),
              q,
              status: props.query?.status,
            },
          });
        case "referral-sources":
          return api.get("./search/referral-sources", {
            query: {
              limit: 20,
              q,
              status: props.query?.status,
              type: props.query?.type,
            },
          });
        case "social-workers":
          return api.get("./search/social-workers", {
            query: {
              limit: 20,
              q,
              status: props.query?.status,
            },
          });
        default:
          return assertNever(props.entity);
      }
    },
  });
  const lookupOptions = mergeSelectedOption(lookupQuery.data?.items ?? [], props.selectedOption);
  const selectOptions = lookupOptions.map((option) => ({
    description: getLookupDescription(option),
    label: formatLookupLabel(option),
    value: option.id,
  }));
  const selectedValue =
    props.value !== null && lookupOptions.some((option) => option.id === props.value)
      ? props.value
      : null;

  return (
    <SharedSelect
      searchable
      allowUnselect={props.allowUnselect}
      buttonProps={props.buttonProps}
      controlledSearchTerm={searchTermState}
      isDisabled={props.isDisabled}
      isLoading={lookupQuery.isFetching}
      label={props.label}
      multiple={false}
      options={selectOptions}
      renderAfter={({ filteredOptions }) =>
        lookupQuery.isFetching === false && filteredOptions.length === 0 ? (
          <Text color={entityProfileModalColors.muted} fontSize="13px" px={4} py={2}>
            No matches
          </Text>
        ) : null
      }
      selectedLabel={(value) =>
        selectOptions.find((option) => option.value === value)?.label ?? props.label
      }
      value={selectedValue}
      width={props.width ?? "260px"}
      onChange={(value) =>
        props.onChange(lookupOptions.find((option) => option.id === value) ?? null)
      }
    />
  );
}

export { formatLookupLabel };
